import type { ProblemDetails } from '../types/dtos';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: ProblemDetails,
    public readonly retryAfterSeconds: number | null,
  ) {
    super(`${problem.code} (${status})`);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const base = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
  const headers = new Headers(init?.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (init?.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  const response = await fetch(`${base}${path}`, { ...init, headers });

  if (response.ok) {
    return (await response.json()) as T;
  }

  const retryAfterRaw = response.headers.get('Retry-After');
  const retryAfterSeconds = retryAfterRaw && /^\d+$/.test(retryAfterRaw) ? Number(retryAfterRaw) : null;

  let problem: ProblemDetails;
  try {
    const text = await response.text();
    problem = text ? (JSON.parse(text) as ProblemDetails) : synthProblem(response.status);
  } catch {
    problem = synthProblem(response.status);
  }
  if (!problem.code || !problem.status) {
    problem = { ...synthProblem(response.status), ...problem };
  }

  throw new ApiError(response.status, problem, retryAfterSeconds);
}

function synthProblem(status: number): ProblemDetails {
  switch (status) {
    case 404:
      return { code: 'job_not_found', title: 'Not Found', status: 404 };
    case 429:
      return { code: 'rate_limited', title: 'Too Many Requests', status: 429 };
    default:
      return { code: 'http_' + status, title: `HTTP ${status}`, status };
  }
}
